import type { InventoryRow } from '../types/inventoryRow';
import { loadInventoryClosure } from './inventoryClosureStorage';

export interface InventoryLineAmountInput {
  quantiteTheorique: number;
  quantiteComptee: number;
  paHt: number;
  pvTtc: number;
}

export interface InventoryAmountEcarts {
  ecartPaHt: number;
  ecartPvTtc: number;
}

export function computeInventoryAmountEcartsFromLines(
  lines: InventoryLineAmountInput[],
): InventoryAmountEcarts {
  let sommePaHtTheorique = 0;
  let sommePaHtReelle = 0;
  let sommePvTtcTheorique = 0;
  let sommePvTtcReelle = 0;

  for (const line of lines) {
    const qtyTheo = Math.max(0, line.quantiteTheorique);
    const qtyCounted = Math.max(0, line.quantiteComptee);
    const unitPaHt = qtyTheo > 0 ? line.paHt / qtyTheo : 0;
    const unitPvTtc = qtyTheo > 0 ? line.pvTtc / qtyTheo : 0;

    sommePaHtTheorique += line.paHt;
    sommePaHtReelle += unitPaHt * qtyCounted;
    sommePvTtcTheorique += line.pvTtc;
    sommePvTtcReelle += unitPvTtc * qtyCounted;
  }

  return {
    ecartPaHt: sommePaHtReelle - sommePaHtTheorique,
    ecartPvTtc: sommePvTtcReelle - sommePvTtcTheorique,
  };
}

function computeProportionalAmountEcartsFromRow(row: InventoryRow): InventoryAmountEcarts {
  if (row.quantiteTheorique === 0) {
    return { ecartPaHt: 0, ecartPvTtc: 0 };
  }

  const ratioDelta = row.quantiteComptee / row.quantiteTheorique - 1;

  return {
    ecartPaHt: row.sommePaHt * ratioDelta,
    ecartPvTtc: row.sommePvTtc * ratioDelta,
  };
}

export function resolveInventoryAmountEcarts(row: InventoryRow): InventoryAmountEcarts | undefined {
  if (row.statut !== 'Clôturé') {
    return undefined;
  }

  const closure = loadInventoryClosure(row.reference);

  if (
    closure &&
    typeof closure.ecartPaHt === 'number' &&
    typeof closure.ecartPvTtc === 'number'
  ) {
    return {
      ecartPaHt: closure.ecartPaHt,
      ecartPvTtc: closure.ecartPvTtc,
    };
  }

  return computeProportionalAmountEcartsFromRow(row);
}

export function getAmountEcartVariant(value: number): 'zero' | 'negative' | 'positive' {
  if (value === 0) {
    return 'zero';
  }

  if (value < 0) {
    return 'negative';
  }

  return 'positive';
}

export function formatSignedAmount(value: number, formatAmount: (amount: number) => string): string {
  const formatted = formatAmount(Math.abs(value));

  if (value > 0) {
    return `+${formatted}`;
  }

  if (value < 0) {
    return `-${formatted}`;
  }

  return formatted;
}
